const DEFAULT_GAP = 12;
const DEFAULT_MARGIN = 16;
/** Arrow inset from the panel's corners (px). */
export const ARROW_EDGE = 16;
/** The roomiest side is still used when it holds this share of the panel. */
const MOST_SPACE_MIN_SHARE = 0.6;
const AUTO_ORDER = ['bottom', 'top', 'end', 'start'];
const OPPOSITE = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
function parsePlacement(placement) {
    if (placement === 'auto')
        return { side: null, align: 'center' };
    const [head, tail] = placement.split('-');
    return { side: head, align: tail ?? 'center' };
}
function toPhysical(side, dir) {
    if (side === 'start')
        return dir === 'rtl' ? 'right' : 'left';
    if (side === 'end')
        return dir === 'rtl' ? 'left' : 'right';
    return side;
}
function clamp(value, min, max) {
    // When the panel is larger than the room, the lower bound wins: its
    // top/left edge stays on screen.
    if (max < min)
        return min;
    return Math.min(Math.max(value, min), max);
}
export function placePanel(input) {
    const { target, panel, viewport, insets, dir } = input;
    const gap = input.gap ?? DEFAULT_GAP;
    const margin = input.margin ?? DEFAULT_MARGIN;
    const minTop = insets.top + margin;
    const maxTop = viewport.height - insets.bottom - margin - panel.height;
    const minLeft = margin;
    const maxLeft = viewport.width - margin - panel.width;
    if (target === null) {
        const room = viewport.height - insets.top - insets.bottom;
        return {
            top: clamp(insets.top + (room - panel.height) / 2, minTop, maxTop),
            left: clamp((viewport.width - panel.width) / 2, minLeft, maxLeft),
            side: 'center',
            arrow: null,
        };
    }
    const targetBottom = target.top + target.height;
    const targetRight = target.left + target.width;
    const space = {
        top: target.top - insets.top - margin,
        bottom: viewport.height - insets.bottom - margin - targetBottom,
        left: target.left - margin,
        right: viewport.width - margin - targetRight,
    };
    const mainDim = (side) => side === 'top' || side === 'bottom' ? panel.height : panel.width;
    const { side: preferred, align } = parsePlacement(input.placement);
    const ordered = [];
    const push = (side) => {
        if (!ordered.includes(side))
            ordered.push(side);
    };
    if (preferred !== null) {
        const physical = toPhysical(preferred, dir);
        push(physical);
        push(OPPOSITE[physical]);
    }
    for (const side of AUTO_ORDER)
        push(toPhysical(side, dir));
    let chosen = ordered.find((side) => space[side] >= mainDim(side) + gap) ?? null;
    if (chosen === null) {
        // The share of the panel each side could hold; the earlier side wins a tie.
        const share = (side) => (space[side] - gap) / Math.max(1, mainDim(side));
        const roomiest = ordered.reduce((best, side) => (share(side) > share(best) ? side : best));
        if (share(roomiest) >= MOST_SPACE_MIN_SHARE)
            chosen = roomiest;
    }
    if (chosen === null) {
        return {
            top: clamp(viewport.height - insets.bottom - margin - panel.height, minTop, maxTop),
            left: clamp((viewport.width - panel.width) / 2, minLeft, maxLeft),
            side: 'overlay',
            arrow: null,
        };
    }
    // An alignment suffix only means something on the axis it names; a
    // `top-start` that had to move beside the target centres instead of guessing.
    const vertical = chosen === 'top' || chosen === 'bottom';
    let crossAlign = 'center';
    if (vertical && (align === 'start' || align === 'end'))
        crossAlign = align;
    if (!vertical && (align === 'top' || align === 'bottom'))
        crossAlign = align;
    let top;
    let left;
    if (vertical) {
        top = clamp(chosen === 'bottom' ? targetBottom + gap : target.top - gap - panel.height, minTop, maxTop);
        // The inline start is the RIGHT edge in RTL.
        const rightEdge = (crossAlign === 'start') === (dir === 'rtl');
        if (crossAlign === 'center')
            left = target.left + target.width / 2 - panel.width / 2;
        else
            left = rightEdge ? targetRight - panel.width : target.left;
        left = clamp(left, minLeft, maxLeft);
    }
    else {
        left = clamp(chosen === 'right' ? targetRight + gap : target.left - gap - panel.width, minLeft, maxLeft);
        if (crossAlign === 'top')
            top = target.top;
        else if (crossAlign === 'bottom')
            top = targetBottom - panel.height;
        else
            top = target.top + target.height / 2 - panel.height / 2;
        top = clamp(top, minTop, maxTop);
    }
    const extent = vertical ? panel.width : panel.height;
    const centre = vertical ? target.left + target.width / 2 - left : target.top + target.height / 2 - top;
    const arrow = centre < 0 || centre > extent || extent < ARROW_EDGE * 2
        ? null
        : { axis: vertical ? 'x' : 'y', offset: clamp(centre, ARROW_EDGE, extent - ARROW_EDGE) };
    return { top, left, side: chosen, arrow };
}
//# sourceMappingURL=position.js.map